import { enemyFrameSet, SpriteFrameSet } from './assets';

export type EnemyId =
  | 'chitauri'
  | 'chitauri-elite'
  | 'chitauri-captain'
  | 'thanos'
  | 'frost-giant'
  | 'frost-giant-elite'
  | 'loki'
  | 'ultron-drone'
  | 'ultron-brute'
  | 'ultron'
  | 'wakandan-merc'
  | 'wakandan-merc-elite'
  | 'klaw'
  | 'killmonger'
  | 'kree-soldier'
  | 'kree-warrior'
  | 'mindless-one'
  | 'mindless-one-elite'
  | 'dormammu'
  | 'oscorp-security'
  | 'electro'
  | 'sandman'
  | 'doc-oc'
  | 'green-goblin'
  | 'venom'
  | 'sentinel'
  | 'brotherhood-mutant'
  | 'magneto'
  | 'nova-corpsman'
  | 'ravager'
  | 'ronan'
  | 'hand-ninja'
  | 'hand-enforcer'
  | 'bullseye'
  | 'kingpin'
  | 'cosmic-drone'
  | 'cosmic-herald'
  | 'silver-surfer'
  | 'galactus'
  | 'putty'
  | 'super-putty'
  | 'goldar'
  | 'rita-repulsa'
  | 'tenga'
  | 'lord-zedd'
  | 'ivan-ooze';

export type EnemySpec = {
  id: EnemyId;
  displayName: string;
  nativeCanvasSize: number;
  idleAnimKey: string;
  hitAnimKey: string;
  bigAttackAnimKey?: string;
  idleFrames: () => SpriteFrameSet;
  hitFrames: () => SpriteFrameSet;
  bigAttackFrames?: () => SpriteFrameSet;
};

function makeEnemy(
  id: EnemyId,
  displayName: string,
  nativeCanvasSize: number,
  hasBigAttack: boolean,
): EnemySpec {
  return {
    id,
    displayName,
    nativeCanvasSize,
    idleAnimKey: `enemy-${id}-idle`,
    hitAnimKey: `enemy-${id}-hit`,
    bigAttackAnimKey: hasBigAttack ? `enemy-${id}-big-attack` : undefined,
    idleFrames: () => enemyFrameSet(id, 'idle'),
    hitFrames: () => enemyFrameSet(id, 'hit'),
    bigAttackFrames: hasBigAttack ? () => enemyFrameSet(id, 'big_attack') : undefined,
  };
}

export const ENEMIES: Record<EnemyId, EnemySpec> = {
  chitauri: makeEnemy('chitauri', 'Chitauri Mook', 92, false),
  'chitauri-elite': makeEnemy('chitauri-elite', 'Chitauri Elite', 92, false),
  'chitauri-captain': makeEnemy('chitauri-captain', 'Chitauri Captain', 92, false),
  thanos: makeEnemy('thanos', 'Thanos', 104, true),
  'frost-giant': makeEnemy('frost-giant', 'Frost Giant', 98, false),
  'frost-giant-elite': makeEnemy('frost-giant-elite', 'Frost Giant Elite', 98, false),
  loki: makeEnemy('loki', 'Loki', 98, true),
  'ultron-drone': makeEnemy('ultron-drone', 'Ultron Drone', 92, false),
  'ultron-brute': makeEnemy('ultron-brute', 'Ultron Brute', 96, false),
  ultron: makeEnemy('ultron', 'Ultron', 104, true),
  'wakandan-merc': makeEnemy('wakandan-merc', 'Wakandan Merc', 92, false),
  'wakandan-merc-elite': makeEnemy('wakandan-merc-elite', 'Wakandan Captain', 98, false),
  klaw: makeEnemy('klaw', 'Klaw', 96, false),
  killmonger: makeEnemy('killmonger', 'Killmonger', 104, true),
  'kree-soldier': makeEnemy('kree-soldier', 'Kree Soldier', 92, false),
  'kree-warrior': makeEnemy('kree-warrior', 'Kree Warrior', 98, false),
  'mindless-one': makeEnemy('mindless-one', 'Mindless One', 92, false),
  'mindless-one-elite': makeEnemy('mindless-one-elite', 'Mindless Elite', 98, false),
  dormammu: makeEnemy('dormammu', 'Dormammu', 104, true),
  'oscorp-security': makeEnemy('oscorp-security', 'Oscorp Security', 92, false),
  electro: makeEnemy('electro', 'Electro', 98, false),
  sandman: makeEnemy('sandman', 'Sandman', 98, false),
  'doc-oc': makeEnemy('doc-oc', 'Doc Oc', 98, false),
  'green-goblin': makeEnemy('green-goblin', 'Green Goblin', 98, false),
  venom: makeEnemy('venom', 'Venom', 104, true),
  sentinel: makeEnemy('sentinel', 'Sentinel', 92, false),
  'brotherhood-mutant': makeEnemy('brotherhood-mutant', 'Brotherhood Mutant', 92, false),
  magneto: makeEnemy('magneto', 'Magneto', 98, true),
  'nova-corpsman': makeEnemy('nova-corpsman', 'Nova Corpsman', 92, false),
  ravager: makeEnemy('ravager', 'Ravager', 92, false),
  ronan: makeEnemy('ronan', 'Ronan the Accuser', 98, true),
  'hand-ninja': makeEnemy('hand-ninja', 'Hand Ninja', 92, false),
  'hand-enforcer': makeEnemy('hand-enforcer', 'Hand Enforcer', 92, false),
  bullseye: makeEnemy('bullseye', 'Bullseye', 92, true),
  kingpin: makeEnemy('kingpin', 'Kingpin', 104, true),
  'cosmic-drone': makeEnemy('cosmic-drone', 'Cosmic Drone', 92, false),
  'cosmic-herald': makeEnemy('cosmic-herald', 'Herald of Galactus', 92, false),
  'silver-surfer': makeEnemy('silver-surfer', 'Silver Surfer', 92, true),
  galactus: makeEnemy('galactus', 'Galactus', 112, true),
  putty: makeEnemy('putty', 'Putty Patroller', 92, false),
  'super-putty': makeEnemy('super-putty', 'Super Putty', 92, false),
  goldar: makeEnemy('goldar', 'Goldar', 92, true),
  'rita-repulsa': makeEnemy('rita-repulsa', 'Rita Repulsa', 92, true),
  tenga: makeEnemy('tenga', 'Tenga Warrior', 92, false),
  'lord-zedd': makeEnemy('lord-zedd', 'Lord Zedd', 92, true),
  'ivan-ooze': makeEnemy('ivan-ooze', 'Ivan Ooze', 92, true),
};
